import { useEffect, useState, useMemo } from 'react';
import AdminLayout from "../../layouts/AdminLayout";
import {
  Box,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Button,
  Flex,
  HStack
} from '@chakra-ui/react';
import adminService from '../../services/adminService';
import { formatDate } from '../../utils/helper';
import ReactPaginate from "react-paginate";
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import { exportToPDF } from '../../utils/pdfExporter';
import { exportToCSV } from '../../utils/csvExporter';

const ITEMS_PER_PAGE = 10;

const Enquiry = () => {

      const [data, setData] = useState([]);
      
      const [loading, setLoading] = useState(true);
      const [search, setSearch] = useState("");
      const [page, setPage] = useState(0);
      const [sortField, setSortField] = useState(null);
      const [sortOrder, setSortOrder] = useState("asc");
    
      useEffect(() => {
        enquires();
      }, []);
    
      const enquires = async () => {
        
        setLoading(true);
        try {
          const res = await adminService.getEnquiryList();
          if (res.statusCode === 200) {
            setData(res.data);
          }
        } catch (error) {
          alert(error.response?.data?.message || "Something went wrong.");
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
    
      const filteredData = useMemo(() => {
        let result = [...data];
        if (search) {
          const lowerSearch = search.toLowerCase();
          result = result.filter((item) =>
          (`${item.FirstName} ${item.LastName}`.toLowerCase().includes(lowerSearch) ||
            (item.Subscription || '').toLowerCase().includes(lowerSearch) ||
            String(item.MobileNo).includes(lowerSearch) ||
            String(item.Email).includes(lowerSearch) ||
            String(item.Subject).includes(lowerSearch) ||
            String(item.Message).includes(lowerSearch) ||
            String(item.CreatedDt).includes(lowerSearch))
          );
        }
        if (sortField) {
          result.sort((a, b) => {
            let aVal = a[sortField] ?? '';
            let bVal = b[sortField] ?? '';
            if (sortField === "PaidAmt") {
              aVal = parseFloat(aVal);
              bVal = parseFloat(bVal);
            }
            return sortOrder === "asc" ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
          });
        }
        return result;
      }, [data, search, sortField, sortOrder]);
    
      const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    
      const currentData = useMemo(() => {
        const start = page * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
      }, [filteredData, page]);
    
      const handleSort = (field) => {
        if (sortField === field) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortField(field);
          setSortOrder("asc");
        }
      };
    
      const handlePageChange = ({ selected }) => {
        setPage(selected);
      };
    
      const exportToCSV1 = () => {
        // data, filename
        exportToCSV(filteredData, "enquiry.csv")
      };
    
      const exportToPDF1 = () => {
        const columns = [
          { label: "Id", accessor: "cId" },
          {
            label: "Fullname",
            accessor: (item) =>
              `${item.FirstName} ${item.LasName}`
          },
          { label: "MobileNo", accessor: "MobileNo" },
          { label: "Email", accessor: "Email" },
          { label: "Subject", accessor: "Subject" },
          { label: "Message", accessor: "Message" },
          { label: "Date", accessor: `CreatedDt` },
        ];
    
        exportToPDF({
          data: filteredData,      // your actual data array
          columns,
          fileName: "enquiry.pdf"
        });
      };

  return (
    <AdminLayout>
          <Box maxW="1000px" mx="auto" px={4}>
            <Box
              bg="white"
              p={6}
              rounded="xl"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
    
              <Flex justify="space-between" mb={4} flexWrap="wrap" gap={4}>
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  maxW="300px"
                  size="sm"
                />
                <HStack>
                  <Button size="sm" colorScheme="blue" leftIcon={<FaFileCsv />} onClick={exportToCSV1}>
                    CSV
                  </Button>
                  <Button size="sm" colorScheme="red" leftIcon={<FaFilePdf />} onClick={exportToPDF1}>
                    PDF
                  </Button>
                </HStack>
              </Flex>
    
              {loading ? (
                <Box textAlign="center" py={10}>
                  <Spinner size="xl" />
                  <Text mt={4}>Loading transactions...</Text>
                </Box>
              ) : (
                <>
    
                  <TableContainer overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th cursor="pointer" onClick={() => handleSort("cId")}>Id</Th>
                          <Th cursor="pointer" onClick={() => handleSort("FirstName")}>FirstName</Th>
                          <Th cursor="pointer" onClick={() => handleSort("LastName")}>LastName</Th>
                          <Th cursor="pointer" onClick={() => handleSort("MobileNo")}>MobileNo</Th>
                          <Th cursor="pointer" onClick={() => handleSort("Email")}>Email</Th>
                          <Th cursor="pointer" onClick={() => handleSort("Subject")}>Subject</Th>
                          <Th cursor="pointer" onClick={() => handleSort("Message")}>Message</Th>
                          <Th cursor="pointer" onClick={() => handleSort("CreatedDt")}>Date</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currentData.length > 0 ? currentData.map((user) => (
                          <Tr key={user.cId}>
                            <Td>{user.cId}</Td>
                            <Td>{user.FirstName}</Td>
                            <Td>{user.LastName}</Td>
                            <Td>{user.MobileNo}</Td>
                            <Td>{user.Email}</Td>
                            <Td>{user.Subject}</Td>
                            <Td>{user.Message}</Td>
                            <Td>{formatDate(user.CreatedDt)}</Td>
                          </Tr>
                        )) : (
                          <Tr>
                            <Td colSpan={10}><Text textAlign="center">No data found</Text></Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
    
                  <Box mt={6}>
                    <ReactPaginate
                      previousLabel="Previous"
                      nextLabel="Next"
                      pageCount={pageCount}
                      onPageChange={handlePageChange}
                      containerClassName="pagination"
                      activeClassName="active"
                      pageClassName="page-item"
                      previousClassName="page-item"
                      nextClassName="page-item"
                      breakClassName="page-item"
                      pageLinkClassName="page-link"
                      previousLinkClassName="page-link"
                      nextLinkClassName="page-link"
                      breakLinkClassName="page-link"
                    />
                  </Box>
    
                  <style>{`
                  .pagination {
                    display: flex;
                    justify-content: center;
                    list-style: none;
                    padding: 0;
                    margin-top: 1rem;
                  }
                  .page-item {
                    margin: 0 5px;
                  }
                  .page-link {
                    padding: 8px 12px;
                    border-radius: 6px;
                    background-color: #e2e8f0;
                    cursor: pointer;
                  }
                  .page-link:hover {
                    background-color: #cbd5e0;
                  }
                  .active .page-link {
                    background-color: #3182ce;
                    color: white;
                  }
                `}</style>
                </>
              )}
            </Box>
          </Box>
        </AdminLayout>
  )
}

export default Enquiry
